import { DebtCardData, debtDocumentStatus, debtDocumentType, debtType } from "../../interfaces/interfaces";

// Every rule here is shared by the debt card (shows the error), the page
// validation (blocks Next) and the checklist (blocks final submit), so they can
// never disagree. They read the server's saved values, which are what the form holds.

// OCR has finished reading a sanction letter on this debt. The balance and
// start date inputs only exist from this point, so only then can they be required.
export const isSanctionLetterProcessed = (debt: DebtCardData) =>
  (debt?.documents || []).some(
    (document) =>
      document.sanctionLetter &&
      document.status !== debtDocumentStatus.processing &&
      document.status !== debtDocumentStatus.failed
  );

// The user said they have debt but no card has a sanction letter uploaded yet
// (a card gets its debtId from that upload). A blank card next to uploaded ones
// is just an unused extra and doesn't count.
export const hasNoUploadedDebt = (debts: DebtCardData[]) => !(debts || []).some((debt) => debt?.debtId);

// true for a combined PDF (sanction letter flagged as containing the schedule)
// and for a separately uploaded repayment schedule
export const hasRepaymentScheduleDocument = (debt: DebtCardData) =>
  (debt?.documents || []).some(
    (document) => document.repaymentSchedule || document.jobType === debtDocumentType.repaymentSchedule
  );

export const isDebtDocumentProcessing = (debt: DebtCardData) =>
  (debt?.documents || []).some(
    (document) => document.status === debtDocumentStatus.processing
  );

// OCR reports the loan product as free text (e.g. "Working Capital Term Loan"),
// so map it to a debt type by keyword. Unknown or ambiguous text returns null,
// which means no mismatch error: an odd phrasing must never block the user.
const REVOLVING_PRODUCT_PATTERN = /\b(overdraft|od|cash credit|cc|revolving|line of credit)\b/i;
const TERM_PRODUCT_PATTERN = /\bterm loan\b/i;

// the loan type the processed sanction letter describes, or null if unknown
export const getSanctionLetterDebtType = (debt: DebtCardData): debtType | null => {
  if (!isSanctionLetterProcessed(debt) || !debt?.productType) return null;
  const looksRevolving = REVOLVING_PRODUCT_PATTERN.test(debt.productType);
  const looksTerm = TERM_PRODUCT_PATTERN.test(debt.productType);
  if (looksRevolving === looksTerm) return null;
  return looksTerm ? debtType.term : debtType.revolving;
};

// the sanction letter's product disagrees with the chosen debt type; the fix is
// removing it and uploading the right one, since the type is fixed at upload
export const isProductMismatch = (debt: DebtCardData) => {
  const letterDebtType = getSanctionLetterDebtType(debt);
  return letterDebtType != null && letterDebtType !== debt?.debtType;
};

// Only term loans need a schedule; revolving loans never do. Skipped on a
// mismatch so we don't ask for a schedule to go with the wrong sanction letter.
export const isRepaymentScheduleMissing = (debt: DebtCardData) =>
  isSanctionLetterProcessed(debt) &&
  !hasRepaymentScheduleDocument(debt) &&
  debt?.debtType === debtType.term &&
  !isProductMismatch(debt);

// a processed debt without its outstanding balance, or a term loan without
// its start date; revolving loans never ask for a start date
export const isDebtDetailsMissing = (debt: DebtCardData) =>
  isSanctionLetterProcessed(debt) &&
  (debt.currentOutstanding == null ||
    (debt.debtType === debtType.term && !debt.actualStartDate));

// Blocks Next, like the financials' missing documents and details: something
// the user still has to provide on this step.
export const isDebtIncomplete = (debt: DebtCardData) =>
  isRepaymentScheduleMissing(debt) || isDebtDetailsMissing(debt);

// Blocks only the checklist's final submit, like a failed financial
// classification: a failed file, or a letter for the wrong loan type. Files
// still processing block neither, same as the financials.
export const hasDebtDocumentError = (debt: DebtCardData) =>
  (debt?.documents || []).some((document) => document.status === debtDocumentStatus.failed) ||
  isProductMismatch(debt);
